import { put, takeLatest, call, select } from 'redux-saga/effects';

import {
  ADD_CS_AGENT,
  SUPPORT_AGENT_UPDATE,
  GET_SUPPORT_AGENT_TEAMS,
  GET_TICKET_ASSIGNMENT_DATA,
} from './actions';
import Utils from '../../../utils/utils';
import * as api from '../../../utils/api';
import { showToaster } from '../../../components/Toaster/action';
import { REQUEST, SUCCESS, FAILURE } from '../../../utils/actionGenerator';

function normalizeAvailabilityCode(raw) {
  const s = String(raw == null ? '' : raw)
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '_');
  if (s === 'ACTIVE' || s === 'ENABLED' || s === 'TRUE' || s === '1') return 'ACTIVE';
  if (
    s === 'NOT_ACTIVE' ||
    s === 'NOTACTIVE' ||
    s === 'INACTIVE' ||
    s === 'DISABLED' ||
    s === 'FALSE' ||
    s === '0'
  ) {
    return 'NOT_ACTIVE';
  }
  if (s === 'IN_PROCESS' || s === 'INPROCESS' || s === 'PROCESSING') return 'IN_PROCESS';
  return s;
}

function availabilityToUiStatus(code) {
  const s = String(code || '').toUpperCase();
  if (s === 'ACTIVE') return 'Present';
  if (s === 'NOT_ACTIVE') return 'Absent';
  if (s === 'IN_PROCESS') return 'Processing....';

  return code ? String(code) : 'Unknown';
}

function mapSupportAgentsResponse(apiResponse, requestParams = {}) {
  const d = apiResponse?.data ?? {};
  const wrapped = d.data !== undefined ? d.data : d;
  const rawAgents = Array.isArray(wrapped.agents) ? wrapped.agents : [];
  const page = wrapped.page ?? requestParams.page ?? 0;
  const size = wrapped.size ?? requestParams.size ?? 10;
  const totalCount = wrapped.totalElements ?? wrapped.totalCount ?? rawAgents.length;
  const totalPages =
    wrapped.totalPages ?? Math.max(1, size > 0 ? Math.ceil((totalCount || 0) / size) : 1);

  const agents = rawAgents.map((agent) => {
    const code = normalizeAvailabilityCode(agent.status ?? agent.availabilityStatus);

    return {
      id: agent.id,
      name: agent.name,
      team: agent.team,
      email: agent.email,
      activeTickets: typeof agent.ticketCount === 'number' ? agent.ticketCount : 0,
      tripCount: typeof agent.tripCount === 'number' ? agent.tripCount : 0,
      availabilityStatus: code,
      status: availabilityToUiStatus(code),
    };
  });

  return {
    page,
    size,
    agents,
    totalPages,
    totalCount,
  };
}

const reAuthenticate = (statusCode) => {
  if (statusCode === 401) {
    Utils.reAuthenticate();
    return;
  }
};

function* getSupportAgentTeamsSaga() {
  try {
    const response = yield call(api.getSupportAgentTeamsRequest);
    const body = response.data || {};
    const root = body.data !== undefined ? body.data : body;
    const rawTeams = root?.teams;
    const list = Array.isArray(rawTeams) ? rawTeams : [];
    const teams = list.map((t) => ({
      type: String(t?.type ?? ''),
      displayValue: String(t?.displayValue ?? t?.display_value ?? ''),
    }));

    const isExplicitFailure = body.success === false;
    const isExplicitSuccess = body.success === true;

    if (isExplicitFailure) {
      yield put({
        type: GET_SUPPORT_AGENT_TEAMS[FAILURE],
        response: body.message || 'Failed to load teams',
      });
    } else if (isExplicitSuccess || list.length > 0) {
      yield put({
        type: GET_SUPPORT_AGENT_TEAMS[SUCCESS],
        response: { teams },
      });
    } else {
      yield put({
        type: GET_SUPPORT_AGENT_TEAMS[FAILURE],
        response: body.message || 'Failed to load teams',
      });
    }
  } catch (error) {
    if (error?.response?.status === 401) {
      reAuthenticate(error?.response?.status);
      return;
    }

    const errorMessage =
      error?.response?.data?.message || error?.message || 'Failed to load teams. Please try again.';

    yield put({
      type: GET_SUPPORT_AGENT_TEAMS[FAILURE],
      response: errorMessage,
    });
  }
}

function* getTicketAssignmentDataSaga(action) {
  try {
    const { page = 0, size = 10, team, email } = action.params || {};

    const response = yield call(api.getTicketAssignmentDataRequest, { page, size, team, email });
    const body = response.data;

    if (body?.success === false) {
      yield put({
        type: GET_TICKET_ASSIGNMENT_DATA[FAILURE],
        response: body.message || 'Failed to fetch agents data',
      });
      return;
    }

    const mappedData = mapSupportAgentsResponse(response, { page, size, team, email });
    yield put({
      type: GET_TICKET_ASSIGNMENT_DATA[SUCCESS],
      response: mappedData,
    });
  } catch (error) {
    if (error?.response?.status === 401) {
      reAuthenticate(error?.response?.status);
      return;
    }

    const errorMessage =
      error?.response?.data?.message || error?.message || 'Failed to fetch agents data. Please try again.';

    yield put({
      type: GET_TICKET_ASSIGNMENT_DATA[FAILURE],
      response: errorMessage,
    });
  }
}

function* refetchAgentsList() {
  const lastQuery = yield select((state) => state?.ticketAssignment?.lastAgentsQuery);
  if (lastQuery) {
    yield put({ type: GET_TICKET_ASSIGNMENT_DATA[REQUEST], params: lastQuery });
  }
}

function* addCsAgentSaga(action) {
  try {
    const response = yield call(api.addCsAgentRequest, action.payload);
    const body = response.data || {};

    if (body.success === false) {
      yield put({
        type: ADD_CS_AGENT[FAILURE],
        response: body.message || 'Failed to add agent',
      });

      yield put(
        showToaster({
          toasterMsg: body.message || 'Failed to add agent',
          toasterType: 'error',
        }),
      );
      return;
    }

    yield put({
      type: ADD_CS_AGENT[SUCCESS],
      response: response.data,
    });
    

    yield put(
      showToaster({
        toasterMsg: 'Agent added successfully!',
        toasterType: 'success',
      }),
    );

    yield call(refetchAgentsList);
  } catch (error) {
    if (error?.response?.status === 401) {
      reAuthenticate(error?.response?.status);
      yield put({ type: ADD_CS_AGENT[FAILURE], response: 'Session expired' });
      return;
    }

    const errorMessage = error?.response?.data?.message || error?.message || 'Failed to add agent. Please try again.';

    yield put({
      type: ADD_CS_AGENT[FAILURE],
      response: errorMessage,
    });

    yield put(
      showToaster({
        toasterMsg: errorMessage,
        toasterType: 'error',
      }),
    );
  }
}

export default function* ticketAssignmentSaga() {
  yield takeLatest(ADD_CS_AGENT[REQUEST], addCsAgentSaga);
  yield takeLatest(SUPPORT_AGENT_UPDATE[REQUEST], supportAgentUpdateSaga);
  yield takeLatest(GET_SUPPORT_AGENT_TEAMS[REQUEST], getSupportAgentTeamsSaga);
  yield takeLatest(GET_TICKET_ASSIGNMENT_DATA[REQUEST], getTicketAssignmentDataSaga);
}

function* supportAgentUpdateSaga(action) {
  const { action: op, id } = action.payload || {};

  try {
    const response = yield call(api.supportAgentUpdateRequest, { action: op, id });
    const body = response.data || {};

    if (body.success === false) {
      const msg = body.message || 'Request failed';
      yield put({ type: SUPPORT_AGENT_UPDATE[FAILURE], response: msg });
      yield put(
        showToaster({
          toasterMsg: msg,
          toasterType: 'error',
        }),
      );
      return;
    }

    yield put({ type: SUPPORT_AGENT_UPDATE[SUCCESS], response: response.data });

    const successMsg =
      op === 'DELETE'
        ? body.message || 'Agent deleted successfully'
        : 'Tickets will be updated within a minute';

    yield put(
      showToaster({
        toasterMsg: successMsg,
        toasterType: 'success',
      }),
    );

    yield call(refetchAgentsList);
  } catch (error) {
    if (error?.response?.status === 401) {
      reAuthenticate(error?.response?.status);
      yield put({ type: SUPPORT_AGENT_UPDATE[FAILURE], response: 'Session expired' });
      return;
    }

    const errorMessage =
      error?.response?.data?.message || error?.message || 'Something went wrong. Please try again.';

    yield put({ type: SUPPORT_AGENT_UPDATE[FAILURE], response: errorMessage });

    yield put(
      showToaster({
        toasterMsg: errorMessage,
        toasterType: 'error',
      }),
    );
  }
}
